import { Box, CircularProgress, Typography } from '@mui/material'
import React, { useState } from 'react'
import PlainModal from '../../../../components/modal/PlainModal'
import { ICoupon } from '../../../../types/leaderboardTypes'
import { RewardItem, WalletBalanceResponse } from '../../../../services/rewardsService'
import { redeemReward, payInWallet } from '../../../../helpers/wallet-helper'
import useAppUtilities from '../../../../hooks/useAppUtilities'
import { selectEater, setMessage, setOpen, setSeverity, fetchEater } from '../../../../store/profileSlice'
import { errorLog } from '../../../../utils/log-helper'
import { useAppSelector } from '../../../../store'
import { mapSubscriptionPlanIdToTier } from '../../../../helpers/tier-helper'
import { logEvent } from '../../../../analytics/analytics'

interface PurchaseCouponProps {
  coupon: ICoupon | null
  reward: RewardItem | null
  open: boolean
  handleClose: () => void
  walletBalance?: WalletBalanceResponse | null
}

const PurchaseCoupon: React.FC<PurchaseCouponProps> = ({
  coupon,
  reward,
  open,
  handleClose,
  walletBalance,
}) => {
  const { dispatch } = useAppUtilities()
  const [purchasing, setPurchasing] = useState(false)
  const eater = useAppSelector(selectEater)
  
  // Determine tier from subscription plan ID
  const mappedPlanName = mapSubscriptionPlanIdToTier(eater?.subscriptionPlanId)
  const isFreeTier = !mappedPlanName || mappedPlanName === null

  // Use reward data if available, otherwise fall back to coupon
  const displayData = reward
    ? {
        image: reward.imageUrl, // Use actual imageUrl from API (can be null)
        title: reward.name,
        description: reward.shortDescription ?? '',
        pointsRequired: reward.pointsCost,
        amountCents: reward.amountCents,
        currency: reward.currency,
      }
    : coupon
      ? {
          image: coupon.image,
          title: coupon.title,
          description: coupon.description,
          pointsRequired: coupon.pointsRequired,
          amountCents: undefined,
          currency: undefined,
        }
      : null

  if (!displayData) return null

  // Compute button enabled state
  const canRedeem = reward?.canRedeem ?? true
  const walletAvailable = (walletBalance?.walletAvailableCents ?? 0)
  const hasEnoughCredits = walletAvailable >= (reward?.amountCents ?? 0)
  // Cash-out is re-gated to subscribers via the backend canRedeem/disableReason (not blanket FE
  // gating): a non-subscriber gets disableReason SUBSCRIPTION_REQUIRED on cash-like rewards.
  const subscriptionRequired = reward?.disableReason === 'SUBSCRIPTION_REQUIRED'
  const isButtonDisabled = !canRedeem || !hasEnoughCredits || purchasing

  const emitRewardRedeemed = (): void => {
    if (!reward) return
    // Redeem is the conversion event of the loyalty loop — previously unmeasured. Emitted only on
    // the success path (both the cash-out and the coupon-redemption flows).
    logEvent('reward_redeemed', {
      reward_type: reward.cashLike ? 'cash' : 'coupon',
      reward_id: reward.code,
      points_spent: reward.pointsCost,
      tier: mappedPlanName ?? undefined,
      screen_name: 'rewards',
    })
  }

  const handleConfirmPurchase = async (): Promise<void> => {
    if (!reward || !canRedeem) return

    setPurchasing(true)
    try {
      if (reward.cashLike) {
        // Handle pay-in-wallet flow
        const response = await payInWallet(reward.code)
        emitRewardRedeemed()
        // Open payment link in new tab
        window.location.href = response.paymentLinkUrl
        dispatch(setOpen(true))
        dispatch(setMessage('Opening payment link...'))
        dispatch(setSeverity('info'))
        handleClose()
      } else {
        // Handle regular redemption
        const result = await redeemReward(reward.code)
        emitRewardRedeemed()
        dispatch(setOpen(true))
        dispatch(setSeverity('success'))
        // Surface the reward in-app: open the claim link so it's claimable even if the account
        // email is fake/undeliverable. Fall back to the email message when no link is available.
        if (result?.claimUrl) {
          dispatch(setMessage('Reward redeemed! Opening your gift to claim…'))
          window.location.href = result.claimUrl
        } else {
          dispatch(setMessage('Reward redeemed! Check your email to claim your gift.'))
        }
        // Refetch eater data to update points
        await dispatch(fetchEater())
        handleClose()
      }
    } catch (error: any) {
      errorLog('Error redeeming reward', error)
      const errorMessage = error?.response?.data?.message || 'Failed to redeem reward. Please try again.'
      dispatch(setOpen(true))
      dispatch(setMessage(errorMessage))
      dispatch(setSeverity('error'))
    } finally {
      setPurchasing(false)
    }
  }

  return (
    <PlainModal
      open={open}
      handleClose={handleClose}
      modelWidth="450px"
      title="Purchase Coupon"
      cancelButtonName="Cancel"
      actionButtonName={purchasing ? 'Processing...' : (subscriptionRequired ? 'Upgrade to cash out' : reward?.cashLike ? 'Cash Out' : 'Purchase')}
      onAction={() => {
        void handleConfirmPurchase()
      }}
      actionButtonDisabled={isButtonDisabled}
    >
      {displayData.image && (
        <Box
          component="img"
          src={displayData.image}
          alt={displayData.title}
          sx={{
            border: 1,
            borderColor: 'grey.200',
            borderRadius: '16px',
            boxShadow: '0px 0px 2px 0px #1A1A1A40',
            width: '100%',
            aspectRatio: 5 / 2.9,
            objectFit: 'cover',
          }}
        />
      )}

      <Box mt={2} mb={4} width="100%">
        <Typography variant="h4" maxWidth={'100%'} noWrap>
          {displayData.title}
        </Typography>
        <Typography
          variant="subtitle1"
          color="grey.600"
          my={0.5}
          sx={{
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {displayData.description}
        </Typography>
      </Box>
      {reward && reward.amountCents > 0 && (
        <Box mt={2}>
          <Typography variant="body2" fontWeight={600}>
            Estimated value: ${(reward.amountCents / 100).toFixed(2)} {reward.currency}
          </Typography>
        </Box>
      )}
      {subscriptionRequired ? (
        <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
          Cash out with Explorer or VIP. Upgrade to cash out, or redeem a gift card instead.
        </Typography>
      ) : (
        !hasEnoughCredits && (
          <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
            {isFreeTier
              ? 'Earn and unlock monthly credits to redeem rewards.'
              : 'Not enough credits in your Rewards Wallet'}
          </Typography>
        )
      )}
      {purchasing && (
        <Box display="flex" justifyContent="center" mt={2}>
          <CircularProgress size={24} />
        </Box>
      )}
    </PlainModal>
  )
}

export default PurchaseCoupon
